import sessionActions from '../actions/SessionActions';
import storeManager from '../store/StoreManager';
import navigationService from './NavigationService';
import alertService from './AlertService';
import { t } from './i18nService';
import settingsReader from '../store/readHelpers/SettingsReader';
import apiService from './api/ApiService';
import timeUtils from '../qti/utils/TimeUtils';
import sessionReader from '../store/readHelpers/SessionReader';
import utilService from './UtilService';
import uiActions from '../actions/UIActions';
import AudioHowlerPlayer from '../loading/resources/AudioHowlerPlayer';
import mediaFinder from '../loading/resources/mediaFinder';
import timeService from './TimeService';
import scorableItemNumberingService from './questionView/ScorableItemNumberingService';
import assessmentReader from '../store/readHelpers/AssessmentReader';
import stateCacheService from './StateCache';
import backgroundAudioActions from '../actions/BackgroundAudioActions';
import backgroundAudioStatus from '../constants/BackgroundAudioStatus';
import clientEventType from '../constants/ClientEventType';
import marketplacePropertiesService from './MarketplacePropertiesService';
import globalsService from './GlobalsService';
import UIConstants from '../constants/UIConstants';

let currentAudioPlayer;

const backgroundAudioService = {
    postPositionSetInterval: undefined,
    postPositionTimeout: 20000,

    getAudioPlayerInstance(assessmentRunId) {
        return AudioHowlerPlayer.getInstanceForAssessmentRunId(assessmentRunId)
            || this.createAssessmentHowlerPlayerInstance(assessmentRunId);
    },

    getFormatFromUrl(url) {
        return url.includes('opus') ? 'opus' : 'mp3';
    },

    start(assessmentRunId) {
        if (!assessmentReader.hasBackgroundAudio(assessmentRunId)) {
            console.warn('No audio resource found');
            return;
        }
        console.info('Background audio initialising', assessmentRunId);
        currentAudioPlayer = this.getAudioPlayerInstance(assessmentRunId);

        console.info('Background audio: playAudio', assessmentRunId);
        const startTime = this.getCurrentTime();
        currentAudioPlayer.playAudio(startTime);
        storeManager.dispatch(uiActions.setBackgroundAudioStarted(true));
        if (sessionReader.isContextExam()) {
            this.startPostingPosition();
        }
    },

    playFromTo(assessmentRunId, audioContentItemId, startTime, endTime) {
        const { backgroundAudio = {} } = storeManager.getState();
        const playingSectionId = this.getPlayingAudionSectionId(backgroundAudio);
        if (playingSectionId && playingSectionId !== audioContentItemId && !sessionReader.isBackgroundAudioPausingAllowed()) {
            return;
        }

        currentAudioPlayer = this.getAudioPlayerInstance(assessmentRunId);
        currentAudioPlayer.seekAudio(startTime);

        backgroundAudioService.storeSectionAudioStatus(audioContentItemId, backgroundAudioStatus.PLAYING);
        this.monitorSectionAudioProgress(audioContentItemId, endTime);

        storeManager.dispatch(uiActions.setBackgroundAudioStarted(true));
        if (sessionReader.isContextExam()) {
            this.startPostingPosition(true);
        }
    },

    createAssessmentHowlerPlayerInstance(assessmentRunId) {
        const { sources, partStartTimes } = assessmentReader.getBackgroundAudioModel(assessmentRunId);

        const urls = sources.map(source => source.url);
        const formats = urls.map(url => this.getFormatFromUrl(url));
        return AudioHowlerPlayer.createInstance(assessmentRunId, this.onEnded, urls, formats, partStartTimes);
    },

    async playAudioForNonChildTest() {
        if (sessionReader.isDemoAAEnabled()) {
            return;
        }

        const { assessment } = storeManager.getState();
        const questionSet = sessionReader.isModeSection() && assessment.sections.length ? assessment.sections[0] : assessment;

        const mediaModel = mediaFinder.getMediaModelForBackgroundAudio(questionSet);
        if (!mediaModel) {
            return;
        }

        const { sources, partStartTimes } = mediaFinder.getMediaModelForBackgroundAudio(questionSet);
        if (!sources) {
            return;
        }

        const urls = sources.map(source => source.url);
        const formats = urls.map(url => this.getFormatFromUrl(url));
        currentAudioPlayer = AudioHowlerPlayer.createInstance(-1, this.onEnded, urls, formats, partStartTimes);

        if (!this.isBackgroundAudioControllable()) {
            storeManager.dispatch(uiActions.setUseBackgroundAudioOverlay(true));
        }
    },

    onEnded() {
        console.info('Background audio ended');
        backgroundAudioService.stopCurrentSectionAudio();
        storeManager.dispatch(uiActions.setUseBackgroundAudioOverlay(false));
        backgroundAudioService.stopPostingPosition(true);
        const hasAudioEnded = typeof currentAudioPlayer?.audioHasEnded === 'function' ? currentAudioPlayer.audioHasEnded() : false;
        if (hasAudioEnded && settingsReader.shouldAllowPauseAndResumeOptionForTestTaker()) {
            storeManager.dispatch(uiActions.setBackgroundAudioStatus(backgroundAudioStatus.ENDED));
        }
    },

    getCurrentTime() {
        const backgroundAudioPosition = utilService.getIntOr(sessionReader.getSessionInfoValue('backgroundAudioPosition'), 0);
        const extraSecondsToRewind = 10; // Starting from latest posted background audio position minus ten seconds.
        if (backgroundAudioPosition > extraSecondsToRewind) {
            return backgroundAudioPosition - extraSecondsToRewind;
        }
        return 0;
    },

    startAudioPlayFromOverlay(startTime, assessmentRunId) {
        if (settingsReader.shouldAllowPauseAndResumeOptionForTestTaker()) {
            console.info('Background audio initialising', assessmentRunId);
            currentAudioPlayer = this.getAudioPlayerInstance(assessmentRunId);
        }
        if (!currentAudioPlayer) {
            console.warn('No audio player initialized when calling startAudioPlayFromOverlay');
            return;
        }
        storeManager.dispatch(uiActions.setBackgroundAudioStarted(true));
        if (settingsReader.shouldAllowPauseAndResumeOptionForTestTaker()) {
            storeManager.dispatch(uiActions.setBackgroundAudioStatus(backgroundAudioStatus.PLAYING));
        }
        if (startTime) currentAudioPlayer.seekAudio(startTime);
        else currentAudioPlayer.resumeAudio();
        if (sessionReader.isContextExam()) {
            this.startPostingPosition();
        }
    },

    cancelAudioPlayFromOverlay() {
        if (!sessionReader.isContextPreview() && !sessionReader.isContextPreviewAudioScript()) {
            return;
        }
        storeManager.dispatch(uiActions.setBackgroundAudioCanceled(true));
    },

    /**
     * storeAudioPositionOnLocalCache(data)
     * Stores the user's background audio playback position in localStorage.
     *
     * This function persists user-specific audio progress for the current
     * assessment. The data is saved under a user-scoped localStorage key to avoid
     * collisions between different users or assessments. The value is serialized
     * to JSON before storage and written using the safe wrapper
     * `globalsService.setItemInLocalStorage()`.
     *
     * Expected structure of `data`:
     * {
     *   assessmentRunId: string | number,     // ID of the assessment this position belongs to
     *   backgroundAudioPosition: number,      // Playback timestamp (e.g., seconds or ms)
     *   userId: string | number               // ID of the current user
     * }
     *
     * This structure enables higher-level logic (if added later) to validate that
     * the saved audio position corresponds to the correct user and current assessment.
     *
     * @param {Object} data - Audio playback metadata to store. Must match the above shape and be JSON-serializable.
     *
     * @returns {void}
    */

    storeAudioPositionOnLocalCache(data) {
        const userSpecificStorageKey = utilService.getUserSpecificLocalStorageKey(
            UIConstants.TEST_TAKER_LOCAL_STORAGE_KEYS.AUDIO_POSITION
        );
        globalsService.setItemInLocalStorage(
            userSpecificStorageKey,
            JSON.stringify(data)
        );
    },

    /**
     * getAudioPositionFromLocalCache()
     * Retrieves the stored background audio playback data for the current user
     * from localStorage.
     *
     * The stored value is retrieved from a user-specific localStorage key. The raw
     * JSON string is returned so that the caller can parse and validate whether:
     * - the saved `assessmentRunId` matches the current assessment, and
     * - the saved `userId` matches the current user.
     *
     * Expected stored JSON format:
     * {
     *   assessmentRunId: string | number,
     *   backgroundAudioPosition: number,
     *   userId: string | number
     * }
     *
     * If the entry does not exist or localStorage is unavailable, `null` is returned.
     *
     * @returns {string|null} A JSON string containing audio position data, or null if not found.
     */

    getAudioPositionFromLocalCache() {
        const userSpecificStorageKey = utilService.getUserSpecificLocalStorageKey(
            UIConstants.TEST_TAKER_LOCAL_STORAGE_KEYS.AUDIO_POSITION
        );
        return globalsService.getItemFromLocalStorage(userSpecificStorageKey);
    },

    startPostingPosition(postInstantly = false) {
        if (postInstantly) {
            this.postBackgroundAudioPosition();
        }

        clearInterval(this.postPositionSetInterval);
        this.postPositionSetInterval = setInterval(() => {
            this.postBackgroundAudioPosition();
        }, this.postPositionTimeout);
    },

    postBackgroundAudioPosition() {
        if (!currentAudioPlayer) {
            console.warn('No audio player initialized when calling postBackgroundAudioPosition');
            return;
        }
        const assessmentRunId = sessionReader.getAssessmentRunId();
        const backgroundAudioPosition = Math.round(currentAudioPlayer.getAudioTime());
        const data = {
            assessmentRunId,
            backgroundAudioPosition,
        };
        const shouldSkipBackendAudioTracking = sessionReader.isDemoAAEnabled();

        if (!shouldSkipBackendAudioTracking) {
            apiService.setSectionAudioPosition(data);
        }
        if (settingsReader.shouldAllowPauseAndResumeOptionForTestTaker()) {
            const audioDataToCache = {
                ...data,
                userId: sessionReader.getUserId(),
            };
            this.storeAudioPositionOnLocalCache(audioDataToCache);
        }
    },

    stopPostingPosition(postLastPosition = true) {
        storeManager.dispatch(uiActions.setBackgroundAudioStarted(false));
        clearInterval(this.postPositionSetInterval);
        if (postLastPosition && sessionReader.isContextExam()) {
            this.postBackgroundAudioPosition();
        }
    },

    stopAndRemoveAudio() {
        if (!currentAudioPlayer) {
            console.warn('No audio player initialized when calling stopAndRemoveAudio');
            return;
        }
        currentAudioPlayer.stopAndRemoveAudio();
        storeManager.dispatch(uiActions.setBackgroundAudioStarted(false));
        this.stopPostingPosition(false);
    },

    async nudgePlayerInstanceCreation(assessmentRunId) {
        return new Promise((resolve) => {
            setTimeout(() => {
                const startTime = Date.now();
                this.createAssessmentHowlerPlayerInstance(assessmentRunId);
                const playerCreationDelay = Math.ceil((Date.now() - startTime) / 1000);

                // Add logs if creating time is more then 3s
                // Ideally it should be executed immediately. Otherwise need to log it to investigate later.
                if (playerCreationDelay > 3) {
                    console.warn(`Background audio player creation took ${playerCreationDelay}s, assessmentRunId - ${assessmentRunId}`);
                }

                resolve();
            });
        });
    },

    pause() {
        if (currentAudioPlayer && !currentAudioPlayer.audioHasEnded()) {
            currentAudioPlayer.pauseAudio();
            if (settingsReader.shouldAllowPauseAndResumeOptionForTestTaker()) {
                storeManager.dispatch(uiActions.setBackgroundAudioStatus(backgroundAudioStatus.PAUSED));
            }
            this.stopPostingPosition();
        }
    },

    resume() {
        if (currentAudioPlayer && !currentAudioPlayer.audioHasEnded()) {
            currentAudioPlayer.resumeAudio();
            storeManager.dispatch(uiActions.setBackgroundAudioStarted(true));
            if (settingsReader.shouldAllowPauseAndResumeOptionForTestTaker()) {
                storeManager.dispatch(uiActions.setBackgroundAudioStatus(backgroundAudioStatus.PLAYING));
            }
            this.startPostingPosition();
        }
    },

    stop() {
        if (currentAudioPlayer) {
            this.stopPostingPosition();
            currentAudioPlayer.stopAudio();
        }
    },

    rewindByMinutes(minutes) {
        const currentAudioPlayer = this.getCurrentAudioPlayer();
        if (!currentAudioPlayer) {
            return;
        }

        const parsedMinutes = isNaN(minutes) ? 0 : minutes;
        const secondsToRewind = parsedMinutes * 60;
        let currentTime = currentAudioPlayer.getAudioTime();
        if (currentTime) {
            const rewindPositionErrorMarginInSeconds = (this.postPositionTimeout / 1000) + 10;
            currentTime = Math.max(currentTime - (secondsToRewind + rewindPositionErrorMarginInSeconds), 0);

            const timeout = settingsReader.getRewindAudioTimeout() / 1000;
            const alert = t('options.rewind-audio.rewindAlert', { minutes: parsedMinutes, timeout });

            this.rewindToPosition(currentTime, alert);
        }
    },

    rewindBySection(partId, extraTime) {
        const currentAudioPlayer = this.getCurrentAudioPlayer();
        if (!currentAudioPlayer) {
            return;
        }

        if (this.isBackgroundAudioControllable()) {
            this.rewindControllableSectionAudio(partId, extraTime);
            return;
        }

        const rewindPosition = currentAudioPlayer.getAudioPartStartTime(partId);
        if (rewindPosition === null) {
            console.error('Rewind part time does not exist');
            return;
        }

        if (rewindPosition > currentAudioPlayer.getAudioTime()) {
            console.error('Not possible to rewind to that position because current position is earlier');
            return;
        }

        const extraMinutes = utilService.getIntOr(extraTime, 0);
        const rewindSection = assessmentReader.getSectionByAdditionalResource(partId);
        if (!rewindSection) {
            console.error('Rewind section can\'t be found');
            return;
        }

        timeService.addTimeInMinutes(extraMinutes);
        const alert = this.getRewoundAlertMessage(partId, extraTime);
        this.rewindToPosition(rewindPosition, alert);
    },

    getCurrentAudioPlayer() {
        if (!currentAudioPlayer) {
            const assessmentRunId = sessionReader.getAssessmentRunId();
            if (!assessmentRunId) {
                console.error('No audio player initialized and no assessmentRunId found when calling rewindBySection/rewindByMinutes');
                return null;
            }
            const backgroundAudioModel = assessmentReader.getBackgroundAudioModel(assessmentRunId);
            if (!backgroundAudioModel) {
                console.error(`Cannot get audio player instance because no background audio is configured for assessmentRunId ${assessmentRunId}`);
                return null;
            }
            currentAudioPlayer = this.getAudioPlayerInstance(assessmentRunId);
        }
        return currentAudioPlayer;
    },

    getRewoundAlertMessage(sectionId, extraTime) {
        const extraMinutes = utilService.getIntOr(extraTime, 0);
        const extraTimeMessage = extraMinutes === 1
            ? t('options.addExtraMinuteAddedConfirmation')
            : t('options.addExtraTimeAddedConfirmation', { minutes: extraMinutes });

        const rewindSection = assessmentReader.getSectionByAdditionalResource(sectionId);
        const sectionName = scorableItemNumberingService.getStimulusFormatNumber(rewindSection.id);
        const timeout = settingsReader.getRewindAudioTimeout();
        return `${t('alert.sectionAudioRewound', { sectionName, timeout: timeout / 1000 })} ${extraTimeMessage}`;
    },

    rewindControllableSectionAudio(sectionId, extraTime) {
        const audioPart = this.getAudioPartStartTimes(sectionId);
        this.pause();
        this.storeSectionAudioStatus(sectionId, backgroundAudioStatus.REWOUND);
        this.storeSectionAudioPosition(sectionId, audioPart.startTime);

        const alert = this.getRewoundAlertMessage(sectionId, extraTime);
        const timeout = settingsReader.getRewindAudioTimeout();
        if (!marketplacePropertiesService.isSecondPopupEnabledForAudioRewind()) {
            timeService.addTimeInMinutes(extraTime);
        }
        alertService.showAlert(alert, { timeout });
    },

    rewindToPosition(position, alert) {
        // Pause during alert
        currentAudioPlayer.pauseAudio();
        const timeout = settingsReader.getRewindAudioTimeout();
        alertService.showAlert(alert, { timeout });

        storeManager.dispatch(uiActions.setBackgroundAudioStarted(false));
        setTimeout(() => {
            currentAudioPlayer.seekAudio(position);
            storeManager.dispatch(uiActions.setBackgroundAudioStarted(true));
            timeService.fetchTimeRemaining(true, true);
        }, timeout);

        // continue posting audio position after rewind
        if (sessionReader.isContextExam()) {
            this.startPostingPosition();
        }
    },

    invigilatorRewind(minutes) {
        storeManager.dispatch(sessionActions.toggleInvigilatorAuthorized(false));
        navigationService.exitFromOptions();
        this.rewindByMinutes(minutes);
        apiService.notifyClientEvent(clientEventType.CLIENT_AUDIO_REWIND, minutes, true).then(() => {
            console.log('Client rewind event sent success');
        }).catch(() => {
            console.warn('Client rewind event sent failed');
        });
    },

    parseTime(time) {
        const { minutes, seconds } = timeUtils.getTimeUnitsFromDuration(time * 1000);
        const formattedMinutes = minutes < 10 ? `0${minutes}` : minutes;
        const formattedSeconds = seconds < 10 ? `0${seconds}` : seconds;
        return `${formattedMinutes}:${formattedSeconds}`;
    },

    showBackgroundAudioTime(duration = currentAudioPlayer.getAudioDuration(), currentTime = currentAudioPlayer.getAudioTime()) {
        if (duration && currentTime) {
            const parsedCurrentTime = this.parseTime(currentTime);
            const parsedDuration = this.parseTime(duration);
            return `${parsedCurrentTime} / ${parsedDuration}`;
        }
        return null;
    },

    getIntroBackgroundAudioPart() {
        const audio = assessmentReader.getBackgroundAudioResource();
        return audio?.partStartTimes?.[0] || null;
    },

    getCurrentSectionBackgroundAudioPart() {
        const audio = assessmentReader.getBackgroundAudioResource();
        if (!audio?.partStartTimes) {
            return null;
        }

        const sectionNumber = assessmentReader.getCurrentSectionNumber();
        return audio.partStartTimes[sectionNumber] || null;
    },

    getAudioPartStartTimes(audioContentItemId) {
        const audio = assessmentReader.getBackgroundAudioResource();
        if (!audio?.partStartTimes) {
            return null;
        }

        return audio.partStartTimes.find(part => part.contentItemId === audioContentItemId);
    },

    getPlayingAudioSectionNumber(backgroundAudio = storeManager.getState().backgroundAudio) {
        const playingAudioId = this.getPlayingAudionSectionId(backgroundAudio);
        return this.getAudioSectionNumber(playingAudioId);
    },

    getAudioSectionNumber(audioContentItemId) {
        const audio = assessmentReader.getBackgroundAudioResource();
        if (!audio?.partStartTimes) {
            return null;
        }

        return audio.partStartTimes.findIndex(part => part.contentItemId === audioContentItemId);
    },

    getPlayingAudionSectionId(backgroundAudio) {
        const playingAudionSectionId = Object.keys(backgroundAudio).find(key => backgroundAudio[key].status === backgroundAudioStatus.PLAYING);
        return playingAudionSectionId ? parseInt(playingAudionSectionId, 10) : null;
    },

    hasPlayingSectionAudio(backgroundAudio = storeManager.getState().backgroundAudio) {
        return Object.keys(backgroundAudio).some(key => backgroundAudio[key].status === backgroundAudioStatus.PLAYING);
    },

    getCurrentSectionAudioStatus() {
        const { backgroundAudio } = storeManager.getState();
        const sectionBackgroundAudioPart = this.getCurrentSectionBackgroundAudioPart();

        if (!sectionBackgroundAudioPart?.contentItemId) {
            return null;
        }

        const status = backgroundAudio[sectionBackgroundAudioPart.contentItemId]?.status;
        return status || backgroundAudioStatus.INIT;
    },

    getCurrentSectionAudioTimesReplayRequested() {
        const sectionBackgroundAudioPart = this.getCurrentSectionBackgroundAudioPart();
        if (!sectionBackgroundAudioPart?.contentItemId) {
            return 0;
        }

        const { backgroundAudio } = storeManager.getState();
        return backgroundAudio[sectionBackgroundAudioPart.contentItemId]?.timesReplayRequested || 0;
    },

    isCurrentSectionAudioPlaying() {
        return this.getCurrentSectionAudioStatus() === backgroundAudioStatus.PLAYING;
    },

    isAudioPlaying(audioContentItemId) {
        const { backgroundAudio } = storeManager.getState();
        return backgroundAudio[audioContentItemId]?.status === backgroundAudioStatus.PLAYING;
    },

    storeSectionAudioStatus(audioContentItemId, status) {
        if (!sessionReader.isDemoAAEnabled()) {
            stateCacheService.getStateCacheInstance().setSectionAudioStatus(audioContentItemId, status);
        }
        storeManager.dispatch(backgroundAudioActions.setSectionAudioStatus(audioContentItemId, status));
    },

    storeSectionAudioPosition(audioContentItemId, position) {
        if (!sessionReader.isDemoAAEnabled()) {
            stateCacheService.getStateCacheInstance().setSectionAudioPosition(audioContentItemId, position);
        }
        storeManager.dispatch(backgroundAudioActions.setSectionAudioPosition(audioContentItemId, position));
    },

    storeSectionAudioIncreasedTimesPlayed(audioContentItemId, timesReplayRequested) {
        if (!sessionReader.isDemoAAEnabled()) {
            stateCacheService.getStateCacheInstance().setSectionAudioTimesReplayRequested(audioContentItemId, timesReplayRequested);
        }
        storeManager.dispatch(backgroundAudioActions.setSectionAudioTimesReplayRequested(audioContentItemId, timesReplayRequested));
    },

    storeIntroPlayed() {
        if (!sessionReader.isDemoAAEnabled()) {
            stateCacheService.getStateCacheInstance().setBackgroundAudioIntroPlayed(true);
        }
        storeManager.dispatch(backgroundAudioActions.setIntroPlayed(true));
    },

    getMonitoringTimeout(endTime) {
        const currentAudioPosition = currentAudioPlayer.getAudioTime();
        return Math.min(1000, Math.round((endTime - currentAudioPosition) * 1000));
    },

    monitorSectionAudioProgress(audioContentItemId, endTime) {
        const timeout = this.getMonitoringTimeout(endTime);

        setTimeout(() => {
            if (!this.isAudioPlaying(audioContentItemId)) {
                return;
            }

            const currentAudioPosition = currentAudioPlayer.getAudioTime();
            storeManager.dispatch(backgroundAudioActions.setSectionAudioPosition(audioContentItemId, currentAudioPosition));

            if (currentAudioPosition >= endTime) {
                this.pause();
                this.storeSectionAudioStatus(audioContentItemId, backgroundAudioStatus.ENDED);
            } else if (this.hasPlayingSectionAudio()) {
                this.updateIntroAudioStatus(currentAudioPosition);
                this.storeSectionAudioPosition(audioContentItemId, currentAudioPosition);
                this.monitorSectionAudioProgress(audioContentItemId, endTime);
            }
        }, timeout);
    },

    updateIntroAudioStatus(currentAudioPosition) {
        const { backgroundAudio } = storeManager.getState();

        if (backgroundAudio.isIntroPlayed || this.getPlayingAudioSectionNumber(backgroundAudio) !== 1) {
            return;
        }

        const introAudioPart = this.getIntroBackgroundAudioPart();
        const introEndTime = introAudioPart.startTime + introAudioPart.duration;
        if (currentAudioPosition >= introEndTime) {
            this.storeIntroPlayed();
        }
    },

    stopCurrentSectionAudio() {
        const sectionBackgroundAudioPart = this.getCurrentSectionBackgroundAudioPart();
        if (!sectionBackgroundAudioPart?.contentItemId) {
            return;
        }
        this.storeSectionAudioStatus(sectionBackgroundAudioPart.contentItemId, backgroundAudioStatus.ENDED);
    },

    checkAudioStatusOnSectionNavigation() {
        if (this.isBackgroundAudioControllable() && this.isCurrentSectionAudioPlaying() && sessionReader.isBackgroundAudioPausingAllowed()) {
            this.pauseCurrentSectionAudio();
        }
    },

    pauseCurrentSectionAudio() {
        const sectionBackgroundAudioPart = this.getCurrentSectionBackgroundAudioPart();
        this.pauseSectionAudio(sectionBackgroundAudioPart.contentItemId);
    },

    pauseSectionAudio(audioContentItemId) {
        backgroundAudioService.pause();
        const currentAudioPlayerTime = currentAudioPlayer.getAudioTime();
        this.storeSectionAudioPosition(audioContentItemId, currentAudioPlayerTime);
        this.storeSectionAudioStatus(audioContentItemId, backgroundAudioStatus.PAUSED);
    },

    isBackgroundAudioControllable() {
        const isBackgroundAudioControllable = sessionReader.isContextExam() &&
            !sessionReader.isContextReadinessCheck() && sessionReader.isBackgroundAudioControlAllowed();
        if (settingsReader.shouldAllowPauseAndResumeOptionForTestTaker()) {
            return isBackgroundAudioControllable;
        }
        return isBackgroundAudioControllable && assessmentReader.hasBackgroundAudioWithParts();
    },
};

export default backgroundAudioService;
